import { AccessReportStatusEnum } from "@app/access-reports/dashboard";
import { AccessReportProviderSetCopyCollectionModel, AccessReportProviderSetCopyResponseModel, AccessReportsAdminLogo } from "@app/access-reports/admin";
import {
  CreateAccessReportsJobsBulkModel,
  EmployeeGroupExample,
} from "@app/access-reports/wizard";
import { SortDirection } from "@app/shared/griddle/enumerators";
import ServiceBase from "@app/shared/serviceBase";
import { AccessReportsProviderSetCopyDashboardResponseModel } from "@app/access-reports/admin/shared/AccessReportsProviderSetCopyDashboardResponseModel";
import { AccessReportsDashboardResponseModel } from "@app/access-reports/dashboard/shared/accessReportsDashboardResponseModel";
import { AccessReport } from "@app/access-reports/shared/accessReport";
import { AccessReportsWizardModel } from "@app/access-reports/shared/accessReportsWizardModel";
import { AccessReportsWizardProviderSet } from "@app/access-reports/shared/accessReportsWizardProviderSet";
import { AccessReportsWizardTemplate } from "@app/access-reports/shared/accessReportsWizardTemplate";
import { AccessReportsWizardVersionModel } from "@app/access-reports/shared/accessReportsWizardVersionModel";
import { ArchiveAccessReportsBulkModel } from "@app/access-reports/shared/archiveAccessReportsBulkModel";
import { CreateAccessReportModel } from "@app/access-reports/wizard/shared/createAccessReportModel";
import { CreateAccessReportsBulkModel } from "@app/access-reports/wizard/shared/createAccessReportsBulkModel";

/** handles communication across the wire when interacting with access reports */
export default class AccessReportsService extends ServiceBase {

  private accessReportsEndpoint = `${this.apiEndpoint}/accessreports`;
  private jobsEndpoint = `${this.apiEndpoint}/jobs`

  public async activateAccessReportsWizardVersion(accessReportWizardId: number, accessReportWizardVersionId: number): Promise<boolean> {
    return await this.makePatch<boolean>(
      `wizards/${accessReportWizardId}/versions/${accessReportWizardVersionId}/activate`,
      null,
      false,
      this.accessReportsEndpoint
    );
  }

  public async archiveAccessReportsWizardVersion(accessReportWizardId: number, accessReportWizardVersionId: number): Promise<boolean> {
    return await this.makePatch<boolean>(
      `wizards/${accessReportWizardId}/versions/${accessReportWizardVersionId}/archive`,
      null,
      false,
      this.accessReportsEndpoint
    );
  }

  public async createAccessReportsWizard(template: AccessReportsWizardTemplate): Promise<AccessReportsWizardModel> {
    return await this.makePost<AccessReportsWizardModel>("wizards", template, this.accessReportsEndpoint);
  }

  public async createNewAccessReportsWizardVersion(
    accessReportWizardId: number,
    template: AccessReportsWizardTemplate
  ): Promise<AccessReportsWizardVersionModel> {
    return await this.makePost<AccessReportsWizardVersionModel>(`wizards/${accessReportWizardId}/versions`, template, this.accessReportsEndpoint);
  }

  public async getAccessReportsWizard(): Promise<AccessReportsWizardModel> {
    return await this.makeGet<AccessReportsWizardModel>("wizards", null, this.accessReportsEndpoint);
  }

  public async getLatestAccessReportsWizardVersion(
    accessReportWizardId: number,
    includeJson: boolean,
  ): Promise<AccessReportsWizardVersionModel> {
    return await this.makeGet<AccessReportsWizardVersionModel>(
      `wizards/${accessReportWizardId}/versions/latestactive`,
      { includeJson },
      this.accessReportsEndpoint
    );
  }

  public async checkInAccessReportsWizardVersion(accessReportWizardId: number, accessReportWizardVersionId: number): Promise<boolean> {
    return await this.makePatch<boolean>(
      `/wizards/${accessReportWizardId}/versions/${accessReportWizardVersionId}/checkin`,
      null,
      false,
      this.accessReportsEndpoint
    );
  }

  public async checkOutAccessReportsWizardVersion(accessReportWizardId: number, accessReportWizardVersionId: number): Promise<boolean> {
    return await this.makePatch<boolean>(
      `/wizards/${accessReportWizardId}/versions/${accessReportWizardVersionId}/checkout`,
      null,
      false,
      this.accessReportsEndpoint
    );
  }

  public async deleteAccessReportsWizardVersion(accessReportWizardId: number, accessReportWizardVersionId: number): Promise<boolean> {
    return await this.makePatch<boolean>(
      `/wizards/${accessReportWizardId}/versions/${accessReportWizardVersionId}/delete`,
      null,
      false,
      this.accessReportsEndpoint
    );
  }

  public async saveAccessReportsWizardVersion(
    accessReportWizardId: number,
    accessReportWizardVersionId: number,
    template: AccessReportsWizardTemplate
  ): Promise<AccessReportsWizardVersionModel> {
    return await this.makePost<AccessReportsWizardVersionModel>(
      `wizards/${accessReportWizardId}/versions/${accessReportWizardVersionId}/save`,
      template,
      this.accessReportsEndpoint
    );
  }

  /**
   * gets all of the access reports for this user
   */
  public async getAccessReports(
    batchCount?: number,
    offset?: number,
    reportSearch?: string,
    includeArchived?: boolean,
    sortDirection?: SortDirection,
    sortedColumn?: string,
  ): Promise<AccessReportsDashboardResponseModel> {
    return await this.makeGet<AccessReportsDashboardResponseModel>("", {
      batchCount,
      includeArchived,
      offset,
      reportSearch,      
      sortDirection,
      sortedColumn,
    }, this.accessReportsEndpoint);
  }

  /**
   * Request for the user's access reports wizard template
   */
  public async getAccessReportsTemplate(): Promise<AccessReportsWizardTemplate> {
    return await this.makeGet<AccessReportsWizardTemplate>("templates/new", null, this.accessReportsEndpoint);
  }

  // should just return a 201 status code
  public async uploadAccessReportsTemplate(template: AccessReportsWizardTemplate): Promise<void> {
    return await this.makePost<void>("templates/new", template, this.accessReportsEndpoint);
  }

  /**
   * Request for a new access report to be created
   * @param createAccessReportModel
   */
  public async createAccessReport(createAccessReportModel: CreateAccessReportModel): Promise<unknown> {
    return await this.makePost<unknown>("", createAccessReportModel, this.accessReportsEndpoint);
  }

  /**
   * Request for multiple, new access reports to be created
   * @param createAccessReportsBulkModel
   */
  public async createAccessReports(createAccessReportsBulkModel: CreateAccessReportsBulkModel): Promise<number[]> {
    return await this.makePost<number[]>("bulk", createAccessReportsBulkModel, this.accessReportsEndpoint);
  }

  /**
   * Request for a job to be created for the given accessReportId
   * @param accessReportId
   */
  public async createAccessReportJob(accessReportId: number): Promise<void> {
    return await this.makePost<void>(`${accessReportId}/submit`, null, this.accessReportsEndpoint);
  }

  /**
   * Request for jobs to be created for multiple access reports
   * @param createAccessReportsJobsBulkModel
   */
  public async createAccessReportsJobs(createAccessReportsJobsBulkModel: CreateAccessReportsJobsBulkModel): Promise<void> {
    return await this.makePost<void>("submit-bulk", createAccessReportsJobsBulkModel, this.accessReportsEndpoint);
  }

  public async createEmployeeGroupFromFile(employeeGroupFile: FormData): Promise<number> {
    return await this.makePost<number>("employee-groups/upload", employeeGroupFile, this.accessReportsEndpoint, true, false, false);
  }

  public async downloadFile(accessReportId: number, fileId: string): Promise<unknown> {
    return await this.makeGet<unknown>(`${accessReportId}/files/${fileId}`, null, this.accessReportsEndpoint, true);
  }

  /**
   * Gets the access report for the given accessReportId
   * @param accessReportId
   */
  public async getAccessReport(accessReportId: number): Promise<AccessReport> {
    return await this.makeGet<AccessReport>(`${accessReportId}`, null, this.accessReportsEndpoint, true);
  }

  /**
   * Archives the selected Access Report
   * @param accessReportId
   */
  public async archiveAccessReport(archiveAccessReportsBulkModel: ArchiveAccessReportsBulkModel): Promise<boolean> {
    return await this.makePatch<boolean>('archive-bulk', archiveAccessReportsBulkModel, false, this.accessReportsEndpoint);
  }

  public async unarchiveAccessReport(unarchiveAccessReportsBulkModel: ArchiveAccessReportsBulkModel): Promise<boolean> {
    return await this.makePatch<boolean>('unarchive-bulk', unarchiveAccessReportsBulkModel, false, this.accessReportsEndpoint);
  }

  public async getEmployeeGroupExamples(): Promise<EmployeeGroupExample[]> {
    return await this.makeGet<EmployeeGroupExample[]>(`employee-groups/examples`, null, this.accessReportsEndpoint, true);
  }

  public async uploadLogo(logoFile: FormData): Promise<AccessReportsAdminLogo> {
    return await this.makePost<AccessReportsAdminLogo>("logos", logoFile, this.accessReportsEndpoint, true, false, false);
  }

  public async archiveLogo(logoId: number): Promise<void> {
    return await this.makePatch<void>(
      `logos/${logoId}/archive`,
      null,
      false,
      this.accessReportsEndpoint
    );
  }

  public async getLogoById(logoId: number): Promise<Blob> {
    return await this.makeGet<Blob>(`logos/${logoId}/download`, null, this.accessReportsEndpoint, true, { responseType: "blob" });
  }

  /**
  * Request for job to be failed
  * @param qesJobId
  * @param accessReportId
  */
  public async failAccessReport(qesJobId: number, accessReportId: number, userId: number): Promise<void> {
    await this.makePost<void>(`${qesJobId}/fail`, `Manually failed job for retry by user ${userId}`, this.jobsEndpoint)
    return await this.makePatch<void>(`${accessReportId}/status/${AccessReportStatusEnum.Failed}`, AccessReportStatusEnum.Failed, false, this.accessReportsEndpoint);

  }

  /**
   * Request for collection of provider sets to be copied
   * @param createAccessReportsBulkModel
   */
  public async createProviderSetCopy(providerSet: AccessReportsWizardProviderSet): Promise<number> {
    return await this.makePost<number>("create-provider-set-copy", providerSet, this.accessReportsEndpoint);
  }

  /**
  * Request to retrieve all provider sets and their statuses for the given client
  */
  public async getProviderSets(
    batchCount?: number,
    offset?: number,
    sortDirection?: SortDirection,
    sortedColumn?: string,
  ): Promise<AccessReportsProviderSetCopyDashboardResponseModel> {
    return await this.makeGet<AccessReportsProviderSetCopyDashboardResponseModel>("provider-set-copies", {batchCount, offset, sortDirection, sortedColumn} , this.accessReportsEndpoint);
  }

  /**
   * Request for collection of provider sets to be copied
   * @param createAccessReportsBulkModel
   */
  public async copyProviderSets(providerSetCopyCollectionModel: AccessReportProviderSetCopyCollectionModel): Promise<AccessReportProviderSetCopyResponseModel[]> {
    return await this.makePost<AccessReportProviderSetCopyResponseModel[]>("copy-provider-sets", providerSetCopyCollectionModel, this.accessReportsEndpoint);
  }
}
